import { acceptInvitation } from "@elation/api-client/src/stitch-web";
import { PracticeTimezone } from "@elation/api-client/src/stitch-web/models/practiceTimezone";

import { navigationV2 } from "@src/shared/navigation/v2";
import { isNetworkError } from "@src/shared/utils/http/errors";
import { captureException } from "@src/shared/utils/observability/sentry";

import { authClient } from "../../../data";
import type {
  EmailVerificationResult,
  ProvisioningPollResult,
  ProvisioningStatus,
  SignUpProvisioningApi,
} from "./auth.provisioning.machine";

const FALLBACK_TIMEZONE = PracticeTimezone["America/Chicago"];
const PRACTICE_TIMEZONES = new Set<string>(Object.values(PracticeTimezone));

function getDefaultTimezone() {
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  return PRACTICE_TIMEZONES.has(timezone) ? timezone : FALLBACK_TIMEZONE;
}

function getEmailVerificationCallbackURL() {
  return `${window.location.origin}${navigationV2.signUp()}?step=5`;
}

async function readVerificationSession(): Promise<EmailVerificationResult> {
  const { data } = await authClient.getSession({
    query: { disableCookieCache: true },
  });

  return {
    verified: Boolean(data?.session && data.user.emailVerified),
    email: data?.session ? data.user.email : null,
  };
}

/**
 * Concrete IO the sign-up provisioning machine orchestrates. The machine
 * declares these as an injected contract ({@link SignUpProvisioningApi})
 * so it stays free of transport/browser details; this is the one place that
 * knows about `authClient`, the network shapes, and Sentry.
 */
export const signUpProvisioningApi: SignUpProvisioningApi = {
  createAccount: async (values) => {
    const response = await authClient.$fetch<{ requestId: string }>(
      "/sign-up/stitch",
      {
        method: "POST",
        body: {
          email: values.email,
          password: values.password,
          firstName: values.firstName,
          lastName: values.lastName,
          specialty: "",
          timezone: getDefaultTimezone(),
          invitationId: values.invitationId,
        },
      },
    );

    if (!response.data?.requestId) {
      throw new Error("Unable to create account.");
    }

    return { email: values.email };
  },
  acceptInvitation: async (invitationId) => {
    await acceptInvitation(invitationId);
  },
  completeSignup: async () => {
    const response = await authClient.$fetch<{ success: boolean }>(
      "/sign-up/stitch/complete",
      { method: "POST" },
    );

    if (!response.data?.success) {
      throw new Error("Failed to complete signup.");
    }
  },
  fetchProvisioningStatus: async (): Promise<ProvisioningPollResult> => {
    try {
      const response = await authClient.$fetch<ProvisioningStatus>(
        "/sign-up/stitch/status",
        { method: "GET" },
      );

      if (response.data) {
        return { kind: "status", status: response.data };
      }

      // better-fetch resolves non-2xx responses to `{ data: null, error }`
      // rather than throwing, so the HTTP status lives on `response.error`.
      const statusCode = (response.error as { status?: number } | null)?.status;
      if (statusCode === 401) return { kind: "unauthorized" };
      if (statusCode === 404) return { kind: "not-found" };
      if (!statusCode) return { kind: "network-error" };

      return { kind: "unexpected-error" };
    } catch (error) {
      if (isNetworkError(error)) return { kind: "network-error" };

      const statusCode = (error as { status?: number }).status;
      if (statusCode === 401) return { kind: "unauthorized" };
      if (statusCode === 404) return { kind: "not-found" };

      return { kind: "unexpected-error" };
    }
  },
  checkEmailVerification: async (): Promise<EmailVerificationResult> => {
    try {
      return await readVerificationSession();
    } catch (error) {
      captureException(error, {
        tags: { route: "v2/sign-up", operation: "checkEmailVerification" },
      });
      throw error;
    }
  },
  readVerificationSession,
  resendVerificationEmail: async (email: string | null): Promise<void> => {
    if (!email) {
      throw new Error(
        "We could not find the email address for this signup. Please go back and try again.",
      );
    }

    try {
      const result = await authClient.sendVerificationEmail({
        email,
        callbackURL: getEmailVerificationCallbackURL(),
      });

      if (result.error) {
        throw new Error(
          result.error.message ?? "Unable to resend verification email.",
        );
      }
    } catch (error) {
      captureException(error, {
        tags: { route: "v2/sign-up", operation: "sendVerificationEmail" },
      });
      throw error;
    }
  },
};
